import { gql } from '@apollo/client';

import { FILE } from './upload';
import { RANK, USER_BASICS } from './user';

export const METRIC_TRIGGER = gql`
  fragment metricTrigger on IdeaMetricTrigger {
    id
    phaseId
    type
    interval
  }
`;

export const PROJECT = gql`
  fragment project on Project {
    id
    name
  }
`;

export const METRIC = gql`
  fragment metric on Metric {
    id
    name
    description
    unit {
      ...unit
    }
  }
`;

export const UNIT = gql`
  fragment unit on MetricUnit {
    id
    label
    type
  }
`;

export const BASELINE = gql`
  fragment baseline on MetricBaseline {
    id
    metric {
      ...metric
    }
    project {
      ...project
    }
    baseline
  }
`;

export const IDEA_METRIC = gql`
  fragment ideaMetric on IdeaMetric {
    id
    project {
      ...project
    }
    metric {
      ...metric
    }
    target
    result
    metricType
  }
`;

export const LIST_METRIC_UNITS = gql`
  query ListMetricUnits {
    listMetricUnits {
      ...unit
    }
  }
  ${UNIT}
`;

export const LIST_METRICS = gql`
  query ListMetrics {
    listMetrics {
      ...metric
    }
  }
  ${METRIC}
  ${UNIT}
`;

export const LIST_PROJECTS = gql`
  query ListProjects {
    listProjects {
      ...project
    }
  }
  ${PROJECT}
`;

/**
 * Get all baselines
 */
export const LIST_BASELINES = gql`
  query ListBaseline {
    listBaseline {
      ...baseline
    }
  }
  ${BASELINE}
  ${METRIC}
  ${PROJECT}
  ${UNIT}
`;

/**
 * Get a single baseline
 */
export const SHOW_BASELINE = gql`
  query ShowBaseline($metricId: UUID!, $projectId: UUID) {
    showBaseline(metricId: $metricId, projectId: $projectId) {
      ...baseline
    }
  }
  ${BASELINE}
  ${METRIC}
  ${PROJECT}
  ${UNIT}
`;

export const LIST_DRIVERS = gql`
  query ListDrivers {
    listDrivers {
      id
      driver
    }
  }
`;

export const GET_METRIC_FILTER_DATA = gql`
  query DashboardMetricFilterData {
    dashboardMetricFilterData {
      channels {
        id
        title
      }
      projects {
        id
        name
      }
      challenges {
        id
        title
      }
      topics {
        id
        name
      }
      businessAreas
      countries {
        id
        name
      }
    }
  }
`;

export const GET_DASHBOARD_METRIC_LIST = gql`
  query DashboardMetricList {
    dashboardMetricList {
      id
      name
    }
  }
`;

export const GET_DASHBOARD_METRIC_STATISTICS = gql`
  query DashboardMetricStatistic(
    $channelId: UUID
    $projectId: UUID
    $challengeId: UUID
    $topicId: Int
    $businessArea: String
    $countryId: Int
    $metricId: UUID
  ) {
    dashboardMetricStatistic(
      channelId: $channelId
      projectId: $projectId
      challengeId: $challengeId
      topicId: $topicId
      businessArea: $businessArea
      countryId: $countryId
      metricId: $metricId
    ) {
      total
      noMetric
      withMetric
      noResult
      withResult
    }
  }
`;

export const GET_DASHBOARD_METRIC_DETAIL = gql`
  query DashboardMetricDetail(
    $channelId: UUID
    $projectId: UUID
    $challengeId: UUID
    $topicId: Int
    $businessArea: String
    $countryId: Int
    $metricId: UUID
  ) {
    dashboardMetricDetail(
      channelId: $channelId
      projectId: $projectId
      challengeId: $challengeId
      topicId: $topicId
      businessArea: $businessArea
      countryId: $countryId
      metricId: $metricId
    ) {
      metric
      type
      ideas
      target
      result
      label
    }
  }
`;

export const GET_DASHBOARD_METRIC_IDEAS = gql`
  query DashboardMetricIdeas(
    $channelId: UUID
    $projectId: UUID
    $challengeId: UUID
    $topicId: Int
    $businessArea: String
    $countryId: Int
    $metricId: UUID
    $page: Int
    $limit: Int
  ) {
    dashboardMetricIdeas(
      channelId: $channelId
      projectId: $projectId
      challengeId: $challengeId
      topicId: $topicId
      businessArea: $businessArea
      countryId: $countryId
      metricId: $metricId
      page: $page
      limit: $limit
    ) {
      items {
        id
        name
        problem
        coverPhoto {
          ...file
        }
        coverPhotoUrl {
          author
          profileUrl
          imageUrl
        }
        createdBy {
          ...userBasics
          rank {
            ...rank
          }
        }
        metrics {
          ...ideaMetric
        }
        currentPhase {
          phase {
            id
            title
          }
        }
      }
      total
    }
  }
  ${FILE}
  ${USER_BASICS}
  ${RANK}
  ${IDEA_METRIC}
  ${METRIC}
  ${PROJECT}
  ${UNIT}
`;

export const LIST_METRIC_TRIGGERS = gql`
  query ListMetricTrigger {
    listMetricTrigger {
      ...metricTrigger
    }
  }
  ${METRIC_TRIGGER}
`;

export const CREATE_IDEA_METRIC = gql`
  mutation CreateIdeaMetric($input: CreateIdeaMetric) {
    createIdeaMetric(input: $input) {
      id
      idea {
        id
        metricTag
      }
    }
  }
`;

export const CREATE_METRIC = gql`
  mutation CreateMetric($input: CreateMetric!) {
    createMetric(input: $input) {
      ...metric
    }
  }
  ${METRIC}
  ${UNIT}
`;

export const CREATE_METRIC_UNIT = gql`
  mutation CreateMetricUnit($input: CreateMetricUnit!) {
    createMetricUnit(input: $input) {
      ...unit
    }
  }
  ${UNIT}
`;

export const CREATE_PROJECT = gql`
  mutation CreateProject($name: String) {
    createProject(name: $name) {
      ...project
    }
  }

  ${PROJECT}
`;

export const CREATE_BASELINE = gql`
  mutation CreateBaseline($input: CreateBaseline!) {
    createBaseline(input: $input) {
      ...baseline
    }
  }
  ${BASELINE}
  ${METRIC}
  ${PROJECT}
  ${UNIT}
`;

export const CREATE_DRIVER = gql`
  mutation CreateDriver($driver: String) {
    createDriver(driver: $driver) {
      id
      driver
    }
  }
`;

export const CREATE_IDEA_METRIC_TRIGGER = gql`
  mutation CreateIdeaMetricTrigger($targetPhase: UUID, $resultPhase: UUID) {
    createIdeaMetricTrigger(
      targetPhase: $targetPhase
      resultPhase: $resultPhase
    ) {
      ...metricTrigger
    }
  }
  ${METRIC_TRIGGER}
`;

export const DELETE_DRIVER = gql`
  mutation DeleteDriver($id: UUID!) {
    deleteDriver(id: $id)
  }
`;

export const UPDATE_METRIC = gql`
  mutation PatchMetric($input: PatchMetric!) {
    patchMetric(input: $input) {
      ...metric
    }
  }
  ${METRIC}
  ${UNIT}
`;

export const UPDATE_BASELINE = gql`
  mutation PatchBaseline($input: PatchBaseline!) {
    patchBaseline(input: $input) {
      ...baseline
    }
  }
  ${BASELINE}
  ${METRIC}
  ${PROJECT}
  ${UNIT}
`;

export const UPDATE_IDEA_METRIC = gql`
  mutation PatchIdeaMetric($input: PatchIdeaMetric!) {
    patchIdeaMetric(input: $input) {
      id
      idea {
        id
        metricTag
      }
    }
  }
`;

export const DELETE_BASELINE = gql`
  mutation DeleteBaseline($id: UUID!) {
    deleteBaseline(id: $id)
  }
`;

export const DELETE_IDEA_METRIC = gql`
  mutation DeleteIdeaMetric($id: UUID!) {
    deleteIdeaMetric(id: $id)
  }
`;

export const DELETE_METRIC = gql`
  mutation DeleteMetric($id: UUID!) {
    deleteMetric(id: $id)
  }
`;
